/**
 * Authentication state.
 *
 * The access token lives only in memory (a ref, not state that re-renders on
 * every request) and is never written to localStorage — there is nothing for a
 * XSS payload or a shared-device browsing history to find. The refresh token
 * never touches JavaScript at all; it travels as an httpOnly cookie the server
 * set, so this file never even sees its value.
 *
 * On load, a silent refresh attempt re-establishes the session from that
 * cookie — which is what lets closing and reopening the installed PWA skip a
 * fresh login every time.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { api, configureApi, ApiClientError } from './api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const accessTokenRef = useRef(null);
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('checking'); // 'checking' | 'authenticated' | 'anonymous'
  const refreshInFlight = useRef(null);

  const setSession = useCallback((accessToken, sessionUser) => {
    accessTokenRef.current = accessToken;
    setUser(sessionUser);
    setStatus(accessToken ? 'authenticated' : 'anonymous');
  }, []);

  const clearSession = useCallback(() => {
    accessTokenRef.current = null;
    setUser(null);
    setStatus('anonymous');
  }, []);

  const login = useCallback(
    async (username, password) => {
      // skipAuthRetry: a wrong password is not an expired session — there is
      // nothing to refresh, and a 401 here should surface as "incorrect
      // username or password" immediately, not trigger a refresh attempt first.
      const result = await api.post('/auth/login', { username, password }, { skipAuthRetry: true });
      setSession(result.accessToken, result.user);
      return result.user;
    },
    [setSession],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined, { skipAuthRetry: true });
    } catch {
      // Even if the network call fails, the local session should still end —
      // the phone is the thing that must stop acting as this user.
    }
    clearSession();
  }, [clearSession]);

  // For changes the account makes to itself (its username, say) that the
  // server has already accepted and echoed back. Replaces the cached user
  // without touching the access token, so nothing has to re-authenticate just
  // because a display name moved.
  const applyUser = useCallback((updated) => {
    setUser((current) => ({ ...current, ...updated }));
  }, []);

  // Deduplicated: if three requests 401 at once, only one refresh call is made
  // and all three retry against its result.
  const doRefresh = useCallback(async () => {
    if (!refreshInFlight.current) {
      // skipAuthRetry is the fix for the deadlock this exact call would
      // otherwise cause: a 401 here (no valid session cookie) must not trigger
      // the same retry-via-refresh logic, since that logic's refresh call is
      // this call — it would end up awaiting its own result and never settle.
      refreshInFlight.current = api
        .post('/auth/refresh', undefined, { skipAuthRetry: true })
        .then((result) => {
          setSession(result.accessToken, result.user);
          return result.accessToken;
        })
        .catch((err) => {
          clearSession();
          throw err;
        })
        .finally(() => {
          refreshInFlight.current = null;
        });
    }
    return refreshInFlight.current;
  }, [setSession, clearSession]);

  useEffect(() => {
    configureApi({
      accessTokenGetter: () => accessTokenRef.current,
      unauthorizedHandler: async (retry) => {
        try {
          await doRefresh();
          return retry();
        } catch {
          return undefined; // let the original 401 propagate as an ApiClientError
        }
      },
    });
  }, [doRefresh]);

  // Silent session restore on first load.
  useEffect(() => {
    doRefresh().catch(() => {
      setStatus('anonymous');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, status, login, logout, applyUser, isAuthenticated: status === 'authenticated' }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export { ApiClientError };
